import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, FormControl, FormLabel, Input, Textarea, Select, Button, Icon, HStack } from "@chakra-ui/react";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SolarContact: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/contact"
        fallbackTitle="Solar Contact"
        fallbackDescription="Contact our solar energy specialists for more information"
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box
        position="relative"
        color="white"
        py={{ base: 16, md: 20 }}
        backgroundImage="url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "rgba(0, 31, 63, 0.85)",
          zIndex: 0
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Heading size="2xl" fontWeight="bold">
              Contact Our Solar Team
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              Ready to start your solar journey? We're here to help
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Contact Form & Info Cards */}
      <Container maxW="container.xl" py={16}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12}>
          {/* Contact Form */}
          <Box>
            <Heading size="lg" mb={6}>Send Us a Message</Heading>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Full Name</FormLabel>
                <Input placeholder="John Smith" size="lg" />
              </FormControl>

              <FormControl>
                <FormLabel>Email Address</FormLabel>
                <Input type="email" placeholder="john@example.com" size="lg" />
              </FormControl>

              <FormControl>
                <FormLabel>Phone Number</FormLabel>
                <Input type="tel" placeholder="04XX XXX XXX" size="lg" />
              </FormControl>

              <FormControl>
                <FormLabel>Service Interest</FormLabel>
                <Select placeholder="Select service" size="lg">
                  <option>Residential Solar</option>
                  <option>Commercial Solar</option>
                  <option>Battery Storage</option>
                  <option>Investment Property</option>
                  <option>System Monitoring</option>
                  <option>General Inquiry</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Message</FormLabel>
                <Textarea
                  placeholder="Tell us about your project..."
                  rows={6}
                  size="lg"
                />
              </FormControl>

              <Button
                bg="#141C49"
                color="white"
                size="lg"
                w="full"
                mt={4}
                _hover={{ bg: "#a07e10" }}
                transition="all 0.2s"
              >
                Send Message
              </Button>
            </VStack>
          </Box>

          {/* Contact Info Cards */}
          <VStack spacing={6} align="stretch">
            <Heading size="lg" mb={2}>Get In Touch</Heading>
            <Text color="gray.600" mb={4}>
              Our solar experts are ready to answer your questions and help you find the perfect system
            </Text>

            <Box bg="white" p={6} borderRadius="lg" border="1px solid" borderColor="gray.200">
              <HStack mb={3}>
                <Icon as={FaPhone} color="#C69214" boxSize={6} />
                <Heading size="sm">Phone</Heading>
              </HStack>
              <Text fontWeight="bold" fontSize="lg">1300 536 159</Text>
              <Text fontSize="sm" color="gray.600">Mon-Fri 8am-6pm, Sat 9am-4pm</Text>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" border="1px solid" borderColor="gray.200">
              <HStack mb={3}>
                <Icon as={FaEnvelope} color="#C69214" boxSize={6} />
                <Heading size="sm">Email</Heading>
              </HStack>
              <Text fontWeight="bold" fontSize="lg">hello@onesolaraustralia.com.au</Text>
              <Text fontSize="sm" color="gray.600">Response within 24 hours</Text>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" border="1px solid" borderColor="gray.200">
              <HStack mb={3}>
                <Icon as={FaMapMarkerAlt} color="#C69214" boxSize={6} />
                <Heading size="sm">Address</Heading>
              </HStack>
              <Text fontWeight="bold">Australia Wide</Text>
              <Text fontSize="sm" color="gray.600">Servicing all major cities and regional areas</Text>
            </Box>
          </VStack>
        </SimpleGrid>
      </Container>

      {/* Office Hours */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <HStack spacing={3}>
              <Icon as={FaClock} boxSize={8} color="#C69214" />
              <Heading size="xl">Office Hours</Heading>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} w="full">
              <Box bg="white" p={6} borderRadius="lg" textAlign="center">
                <Text fontWeight="bold" mb={2}>Monday - Friday</Text>
                <Text fontSize="2xl" color="#141C49" fontWeight="bold">8:00 AM - 6:00 PM</Text>
              </Box>

              <Box bg="white" p={6} borderRadius="lg" textAlign="center">
                <Text fontWeight="bold" mb={2}>Saturday</Text>
                <Text fontSize="2xl" color="#141C49" fontWeight="bold">9:00 AM - 4:00 PM</Text>
              </Box>

              <Box bg="white" p={6} borderRadius="lg" textAlign="center">
                <Text fontWeight="bold" mb={2}>Sunday</Text>
                <Text fontSize="2xl" color="gray.400" fontWeight="bold">Closed</Text>
              </Box>
            </SimpleGrid>

            <Text color="gray.600" textAlign="center" maxW="2xl">
              For urgent matters outside business hours, please send an email and we'll respond first thing the next business day
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* CTA */}
      <Container maxW="container.xl" py={16}>
        <Box
          bg="gray.800"
          p={12}
          borderRadius="2xl"
          textAlign="center"
          color="white"
        >
          <Heading size="xl" mb={4}>Prefer to Speak with Someone?</Heading>
          <Text fontSize="lg" mb={6} color="gray.300">
            Call us now for immediate assistance from our solar specialists
          </Text>
          <Button as="a" href="tel:1300536159" size="lg" bg="white" color="#141C49" _hover={{ bg: "#a07e10", color: "white" }} transition="all 0.2s">
            Call 1300 536 159
          </Button>
        </Box>
      </Container>

      <SolarFooter />
    </Box>
  );
};

export default SolarContact;
